import { User } from "@auth0/auth0-react";
import type { PartnerTokenLoginResponse } from "@pandas/http-client";
import { platformApi } from "@/services";
import { useStore } from "@/store";
import { getAuth0UserLocationId } from "@/utils/auth0";
import { buildQueryString, getSearchParam, type SearchParamsSource } from "@/utils/searchParams";

export const getInstanceInfo = async ({
	locationId,
	partnerCode,
	partnerToken,
}: {
	locationId?: string;
	partnerCode?: string;
	partnerToken?: string;
}) => {
	if (locationId) {
		const locationAndPartnerData = await platformApi.getLocationAndPartner(locationId);
		return { partner: locationAndPartnerData.Partner, location: locationAndPartnerData, partnerToken };
	}
	if (partnerCode) {
		const partnerResponse = await platformApi.getPartner(partnerCode);

		return { partner: partnerResponse, location: undefined, partnerToken: partnerToken };
	}
	throw new Error("Cannot fetch instance info: missing both locationId and partnerCode");
};

export const authenticateWithPartnerToken = async (partnerToken: string): Promise<PartnerTokenLoginResponse> => {
	return await platformApi.logInWithPartnerToken(partnerToken);
};

type GetInitializationParamsInput = {
	searchParams: SearchParamsSource;
	user: User | null | undefined;
	routePartnerCode?: string;
};

const canReuseStoredInitializationParams = (
	routePartnerCode: string | undefined,
	storedPartnerCode: string | undefined,
) => !routePartnerCode || routePartnerCode === storedPartnerCode;

export const getInitializationParams = ({ searchParams, user, routePartnerCode }: GetInitializationParamsInput) => {
	const { instanceInfo, sessionId: storedSessionId } = useStore.getState();
	const storedPartnerCode = instanceInfo?.partner.code;
	const storedParams = canReuseStoredInitializationParams(routePartnerCode, storedPartnerCode)
		? {
				locationId: instanceInfo?.location?.id,
				partnerCode: storedPartnerCode,
				partnerToken: instanceInfo?.partnerToken,
				sessionId: storedSessionId,
			}
		: {};

	let locationId = getSearchParam(searchParams, "locationId") || storedParams.locationId;
	const partnerToken = getSearchParam(searchParams, "partnerToken") || storedParams.partnerToken;
	const explicitPartnerCode = getSearchParam(searchParams, "partnerCode") || storedParams.partnerCode;
	const sessionId = getSearchParam(searchParams, "sessionId") || storedParams.sessionId;

	// Fall back to the authenticated user's location claim only when no identifying
	// info was provided via search params or stored state. routePartnerCode is the
	// URL path segment and is always present for partner-routed users, so it must
	// not gate this fallback.
	if (!locationId && !partnerToken && !explicitPartnerCode) {
		locationId = getAuth0UserLocationId(user);
	}

	const partnerCode = explicitPartnerCode || routePartnerCode;

	return { locationId, partnerToken, partnerCode, sessionId };
};

export type InitializationParams = {
	sessionId?: string | null;
	partnerToken?: string;
	partnerCode?: string;
	locationId?: string;
};

export function getRedirectToDeviceSearch({ sessionId, partnerToken, partnerCode, locationId }: InitializationParams) {
	return {
		...(sessionId ? { sessionId } : {}),
		...(partnerToken ? { partnerToken } : {}),
		...(partnerCode ? { partnerCode } : {}),
		...(locationId ? { locationId } : {}),
	};
}

export function getRedirectToDeviceParams({ sessionId, partnerToken, partnerCode, locationId }: InitializationParams) {
	return buildQueryString(getRedirectToDeviceSearch({ sessionId, partnerToken, partnerCode, locationId }));
}
